import {
  AreaChart as RechartsAreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
} from 'recharts';
import { ChartContainer } from './ChartContainer';
import { ChartTooltipContent } from './ChartTooltip';
import type { ChartConfig } from './chart-utils';
import { axisLabel, axisMargin, legendStyle } from './chart-utils';
import { CategoryTick, categoryAxis } from './axis-ticks';

interface AreaChartProps {
  data: Record<string, unknown>[];
  config: ChartConfig;
  xAxisKey: string;
  stacked?: boolean;
  height?: number;
  unit?: string;
  /** Achsentitel/Einheit der X-Achse. */
  xUnit?: string;
  /** Achsentitel/Einheit der Y-Achse. */
  yUnit?: string;
  className?: string;
  ariaLabel?: string;
  gradient?: boolean;
  /** Erzwungene Nachkommastellen im Tooltip. `undefined` = auto (0–2). */
  decimals?: number;
}

/**
 * AreaChart - Flaechendiagramm mit optionalem Stacking und Gradienten.
 *
 * @example Einfache Daten:
 * data={[{ month: 'Jan', strom: 4000 }, { month: 'Feb', strom: 3000 }]}
 * config={{ strom: { label: 'Strom', color: 'oklch(60% 0.2 260)' } }}
 *
 * @example Genestete/Gestapelte Daten:
 * data={[{ month: 'Jan', strom: 4000, gas: 2400, waerme: 1800 }]}
 * config={{ strom: {...}, gas: {...}, waerme: {...} }}
 * stacked={true}
 */
export function CPAreaChart({
  data,
  config,
  xAxisKey,
  stacked = false,
  height = 350,
  unit,
  xUnit,
  yUnit,
  className,
  ariaLabel,
  gradient = true,
  decimals,
}: AreaChartProps) {
  const dataKeys = Object.keys(config);
  const valueLabel = yUnit ?? unit;

  return (
    <ChartContainer config={config} height={height} className={className} ariaLabel={ariaLabel}>
      {(containerWidth) => {
        const { angled, height: categoryAxisHeight } = categoryAxis({ data, xAxisKey, containerWidth, xUnit });
        return (
          <RechartsAreaChart data={data} margin={axisMargin({ xUnit, yUnit: valueLabel })}>
            <CartesianGrid strokeDasharray="3 3" stroke="var(--color-brand-border, #e5e7eb)" />
            <XAxis
              dataKey={xAxisKey}
              interval={0}
              tick={<CategoryTick angled={angled} />}
              height={categoryAxisHeight}
              tickLine={false}
              axisLine={false}
              label={axisLabel(xUnit, 'x')}
            />
            <YAxis
              tick={{ fontSize: 12, fill: 'var(--color-brand-muted, #6b7280)' }}
              tickLine={false}
              axisLine={false}
              label={axisLabel(valueLabel, 'y')}
            />
            <Tooltip content={<ChartTooltipContent config={config} unit={unit} decimals={decimals} />} />
            {/* itemSorter=null: Recharts sortiert die Legende per Default alphabetisch
                nach series_key ('value'); null behält die Config-/Editor-Reihenfolge. */}
            <Legend
              itemSorter={null}
              iconType="circle"
              iconSize={8}
              wrapperStyle={legendStyle(xUnit)}
              formatter={(value: string) => config[value]?.label ?? value}
            />
            {gradient && (
              <defs>
                {dataKeys.map((key) => (
                  <linearGradient key={key} id={`gradient-${key}`} x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor={`var(--color-${key})`} stopOpacity={0.3} />
                    <stop offset="95%" stopColor={`var(--color-${key})`} stopOpacity={0} />
                  </linearGradient>
                ))}
              </defs>
            )}
            {dataKeys.map((key) => (
              <Area
                key={key}
                type="monotone"
                dataKey={key}
                stackId={stacked ? 'stack' : undefined}
                stroke={`var(--color-${key})`}
                fill={gradient ? `url(#gradient-${key})` : `var(--color-${key})`}
                fillOpacity={gradient ? 1 : 0.1}
                strokeWidth={2}
              />
            ))}
          </RechartsAreaChart>
        );
      }}
    </ChartContainer>
  );
}
